import React from "react";
import { Typography, Box } from "@mui/material";
import SimplePaper from "../../../components/SimplePaper";
import { MathComponent } from "mathjax-react";

const D4fromD1 = () => {
  const title = "Calculation of D4 (Chaturthamsa) from D1 (Rasi)";

  const body = (
    <>
      <Typography variant="body1" paragraph>
        The D4 chart, or Chaturthamsa chart, is used in Vedic astrology to
        analyze property, land, home, and emotional well-being. Below is the
        step-by-step process to calculate the D4 chart from the D1 (Rasi) chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Procedure to Calculate D4 (Chaturthamsa) from D1 (Rasi)
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Planet's Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        First, identify the degree of the planet in the D1 chart. For example,
        if Venus is located at 18° Taurus, this degree will be used for the
        calculation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Divide the Sign into Four Parts
      </Typography>

      <Typography variant="body1" paragraph>
        Each zodiac sign is divided into four equal parts, each spanning 7.5°.
        To find the Chaturthamsa position of a planet, we divide its degree in
        the sign by 7.5.
      </Typography>

      <Box sx={{ textAlign: "center", margin: "1rem 0" }}>
        <MathComponent
          tex={String.raw`Chaturthamsa \ Position = \frac{Degree \ in \ Sign}{7.5}`}
        />
      </Box>

      <Typography variant="body1" paragraph>
        Depending on the result, we can determine the section in which the
        planet lies:
      </Typography>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`0 \leq \frac{Degree \ in \ Sign}{7.5} < 1`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>first 7.5°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`1 \leq \frac{Degree \ in \ Sign}{7.5} < 2`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>second 7.5°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`2 \leq \frac{Degree \ in \ Sign}{7.5} < 3`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>third 7.5°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`3 \leq \frac{Degree \ in \ Sign}{7.5} < 4`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>fourth 7.5°</strong>.
        </Typography>
      </Box>

      <Typography variant="h5" gutterBottom>
        3. Assign the Chaturthamsa Based on the Planet's Degree
      </Typography>

      <Typography variant="body1" paragraph>
        <strong>First 7.5°:</strong> The planet is assigned to the{" "}
        <strong>same sign</strong> as the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Second 7.5°:</strong> The planet is assigned to the{" "}
        <strong>4th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Third 7.5°:</strong> The planet is assigned to the{" "}
        <strong>7th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fourth 7.5°:</strong> The planet is assigned to the{" "}
        <strong>10th sign</strong> from the D1 sign.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Example: Calculating Venus at 18° Taurus
      </Typography>

      <Typography variant="body1" paragraph>
        Taurus is the D1 sign. Venus at 18° falls in the third 7.5° (between 15°
        and 22.5°). Therefore, it belongs to the <strong>7th sign</strong> from
        Taurus, which is <strong>Scorpio</strong>
      </Typography>

      <Typography variant="h4" gutterBottom>
        Python Program to Calculate D4 (Chaturthamsa) from D1
      </Typography>

      <Typography variant="body1" paragraph>
        Below is a Python program that performs the calculation of the D4
        (Chaturthamsa) position from the D1 (Rasi) chart, using the planet's
        degree and sign in the D1 chart.
      </Typography>

      <Box
        component="pre"
        sx={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <code>
          {`def calculate_chaturthamsa(degree, sign):
    # List of zodiac signs in order
    zodiac_signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces']

    # Get the index of the current sign in the zodiac cycle
    sign_index = zodiac_signs.index(sign)

    # Determine which Chaturthamsa the planet is in based on its degree
    if degree <= 7.5:
        # First 7.5° belongs to the same sign
        chaturthamsa_sign = zodiac_signs[sign_index]
    elif 7.5 < degree <= 15:
        # Second 7.5° belongs to the 4th sign from the current sign
        chaturthamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 15 < degree <= 22.5:
        # Third 7.5° belongs to the 7th sign from the current sign
        chaturthamsa_sign = zodiac_signs[(sign_index + 6) % 12]
    else:
        # Fourth 7.5° belongs to the 10th sign from the current sign
        chaturthamsa_sign = zodiac_signs[(sign_index + 9) % 12]

    return chaturthamsa_sign

# Example usage
planet = "Venus"
degree = 18
sign = "Taurus"

chaturthamsa_sign = calculate_chaturthamsa(degree, sign)
print(f"{planet} at {degree}° {sign} in the D1 chart is placed in {chaturthamsa_sign} in the D4 chart.")`}
        </code>
      </Box>

      <Typography variant="body1" paragraph>
        The above Python program calculates the Chaturthamsa for a planet based
        on its degree and sign in the D1 chart. The program divides the sign
        into four equal parts and assigns the Chaturthamsa based on the planet's
        degree.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Explanation of the Formula Used in the Python Program
      </Typography>

      <Typography variant="body1" paragraph>
        The program divides the sign into four parts: the first 7.5°, the second
        7.5°, the third 7.5°, and the fourth 7.5°. It assigns the Chaturthamsa
        based on the planet's degree within the sign:
        <br />
        - First 7.5° belongs to the same sign.
        <br />
        - Second 7.5° belongs to the 4th sign from the D1 sign.
        <br />
        - Third 7.5° belongs to the 7th sign from the D1 sign.
        <br />- Fourth 7.5° belongs to the 10th sign from the D1 sign.
      </Typography>

      <Typography variant="body1" paragraph>
        This method programmatically reproduces the manual calculation and can
        be easily adjusted for different planets, degrees, and signs.
      </Typography>
    </>
  );

  return <SimplePaper title={title} body={body} />;
};

export default D4fromD1;
